import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// Instructions the clinician types while recording ("insert normal cardiac
// exam", "keep the plan short"). Sent once with the visit; after the note is
// written the server applies them, together with any "insert <shortcut>" the
// clinician said near the end of the recording, as one note edit.
//
// Sending the field (even as an empty list) asks the server to do this for
// the visit, including the spoken shortcut cue. Clients that apply
// instructions themselves leave it out, so nothing is applied twice.
export const RECORDING_INSTRUCTIONS_MAX = 10;
export const RECORDING_INSTRUCTION_MAX_LENGTH = 500;

export const recordingInstructionSchema = z
  .string()
  .trim()
  .min(1)
  .max(RECORDING_INSTRUCTION_MAX_LENGTH);
export const recordingInstructionsSchema = z
  .array(recordingInstructionSchema)
  .max(RECORDING_INSTRUCTIONS_MAX)
  // The same instruction typed twice is applied once.
  .transform((items) =>
    items.filter(
      (item, index) =>
        items.findIndex(
          (other) => other.toLowerCase() === item.toLowerCase(),
        ) === index,
    ),
  );
export type RecordingInstructions = z.output<
  typeof recordingInstructionsSchema
>;
// Spoken shortcut cues the clinician dismissed while recording ("Will insert:
// … ×"): saved shortcut names the server must not insert from the cue.
export const skipShortcutsSchema = z
  .array(z.string().trim().min(1).max(64))
  .max(20);

export const VISIT_INSTRUCTION_STATES = [
  // No instructions were sent with this visit.
  "none",
  // Waiting for the note, or being applied.
  "pending",
  "applied",
  // The note was ready and there was nothing to apply.
  "nothing",
  // Could not be applied (the note failed, or the edit kept failing).
  "failed",
  // The clinician undid the applied edit.
  "undone",
] as const;
export const NOT_APPLIED_REASONS = [
  // The edit would have changed clinical facts the instruction did not name;
  // the clinician can still make it with AI Edit, where they review it.
  "fact_check",
  // The edit service did not answer.
  "unavailable",
] as const;

export const visitInstructionsSchema = z.object({
  state: z.enum(VISIT_INSTRUCTION_STATES),
  // What the clinician typed, in order.
  instructions: z.array(z.string()).max(RECORDING_INSTRUCTIONS_MAX),
  applied: z.object({
    // Saved shortcuts inserted because the clinician said "insert <name>"
    // near the end of the recording or named them in an instruction.
    shortcuts: z
      .array(z.object({ name: z.string(), section: z.string() }))
      .max(50),
    instructions: z.array(z.string()).max(RECORDING_INSTRUCTIONS_MAX),
  }),
  notApplied: z
    .array(
      z.object({
        text: z.string(),
        reason: z.enum(NOT_APPLIED_REASONS),
      }),
    )
    .max(RECORDING_INSTRUCTIONS_MAX + 50),
  // True while the note is exactly as the instructions left it.
  canUndo: z.boolean(),
});
export type VisitInstructions = z.output<typeof visitInstructionsSchema>;

const errors = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Visit not found" },
  CONFLICT: { message: "The note changed after the instructions" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Recording instructions unavailable" },
});
const meta = (method: "GET" | "POST", path: `/${string}`) =>
  openapi({
    method,
    path,
    inputStructure: "detailed",
    successStatus: 200,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
const params = z.strictObject({ public_id: publicIdSchema });
const query = z.strictObject({}).default({});
export const recordingInstructionsContract = {
  // What was applied to the visit's note (or is still waiting for it).
  getVisitInstructions: errors
    .input(z.object({ params, query }))
    .meta(meta("GET", "/api/v2/visits/{public_id}/instructions"))
    .output(visitInstructionsSchema),
  // Puts the note back as it was before the instructions were applied, while
  // nobody has changed it since. The applied version stays in the history.
  undoVisitInstructions: errors
    .input(z.object({ params, query, body: z.undefined().optional() }))
    .meta(meta("POST", "/api/v2/visits/{public_id}/instructions/undo"))
    .output(visitInstructionsSchema),
};
